/**
 * 项目物料 API 接口定义
 *
 * 对接文档：《SCP服务商提报结果同步-前端对接接口文档》
 * 基础路径：/mdm-portal（通过网关 /api 代理转发）
 *
 * ======================== 接口总览 ========================
 * 1. POST /open/srm/projectMaterial/getPage           - 项目物料分页查询
 * 2. POST /open/srm/projectMaterial/getAuditRecords   - 项目物料审核记录
 * 3. POST /open/platformProduct/getThirdOnShelf        - 批量获取客户上下架状态（提报管理列表用）
 * =========================================================
 */

import { GetLocalStorage } from '@/tools'
import request from '../tools/request'
import { HTTP } from './http'
const { GET, POST } = request

// ====================== 客户平台状态枚举 ======================

/** 客户平台状态值 → 中文名映射 */
export const THIRD_ON_SHELF_MAP: Record<string, string> = {
  '1': '已上架',
  '2': '已下架',
  '3': '审核中',
  '4': '已驳回',
  '5': '已冻结',
}

// ====================== 数据类型定义 ======================

export interface PageResultProjectMaterialPageDTO {
  /**
   * 分页内容
   */
  content?: ProjectMaterialDTO[]
  /**
   * 当前页码
   */
  pageNo?: number
  /**
   * 分页尺寸
   */
  pageSize?: number
  /**
   * 数据总量
   */
  totalCount?: number
  /**
   * 总页数
   */
  totalPages?: number
  [property: string]: any
}

/** 项目物料分页查询 - 请求参数（Body JSON） */
export interface ProjectMaterialPageParams {
  supplierCode?: string // 服务商编码
  page?: number // 页码，从 1 开始
  limit?: number // 每页条数
  platformCode: string // 项目编码（必填）
  productCodes?: string // 咸亨SKU编码，多个用逗号分隔，上限500
  customerSkuCodes?: string // 客户SKU编码，多个用逗号分隔，上限500
  customerSpuCodes?: string // 客户SPU编码，多个用逗号分隔，上限500
  customerMaterialCodes?: string // 客户物料编码，多个用逗号分隔，上限500
  thirdOnShelf?: string // 客户平台状态：1-已上架 2-已下架 3-审核中 4-已驳回 5-已冻结
  thirdOnShelfStartTime?: number // 状态变更开始时间（毫秒时间戳）
  thirdOnShelfEndTime?: number // 状态变更结束时间（毫秒时间戳）
  productName?: string // 咸亨物料名称（模糊搜索）
  [property: string]: any
}

export interface Pages {}

/** 项目物料列表项（ProjectMaterialDTO） */
export interface ProjectMaterialDTO {
  productCode: string // 咸亨SKU编码
  customerSkuCode: string // 客户SKU编码
  customerSpuCode: string // 客户SPU编码
  customerMaterialCode: string // 客户物料编码
  customerMaterialUnit: string // 客户物资编码单位
  thirdOnShelf: string // 客户平台状态：1-已上架 2-已下架 3-审核中 4-已驳回 5-已冻结
  thirdOnShelfTime: string // 状态变更时间（字符串格式，如 "2026-04-10 14:30:00"）
  pushPrice: string // 推送协议价
  brandName: string // 品牌
  productName: string // 名称
  manuCode: string // 规格/型号
  unitName: string // 基本单位
  description: string // 描述
  [property: string]: any
}

/** 项目物料审核记录（ProjectMaterialAuditRecordDTO） */
export interface ProjectMaterialAuditRecordDTO {
  checkType: string // 审核类型（如：初审上架、新增上架审核、下架通知等）
  checkTypeDesc: string // 审核类型说明
  checkResult: string // 审核结果（如：通过、未通过）
  checkResultDesc: string // 审核结果描述
  checkTime: number // 审核时间（毫秒时间戳）
  remark: string // 审核说明

  [property: string]: any
}

// ====================== API 接口函数 ======================

/**
 * 1.1 项目物料分页查询
 *
 * POST /open/srm/projectMaterial/getPage
 *
 * @param supplierCode 服务商编码（Query 参数）
 * @param params 分页 + 筛选参数（Body JSON）
 */
export const POST_PROJECT_MATERIAL_PAGE = (params: ProjectMaterialPageParams) =>
  POST<PageResultProjectMaterialPageDTO>(
    `${HTTP}/projectMaterial/getPage`,
    {
      ...params,
      supplierCode: GetLocalStorage('SUPPLIER_MDM_CODE'),
    },
    'application/json',
  )

/**
 * 1.2 查询项目物料审核记录

 * 返回按审核时间倒序排列的列表（非分页）
 *
 * @param platformCode 项目编码
 * @param productCode 物料编码（咸亨SKU编码）
 */
export const POST_PROJECT_MATERIAL_AUDIT_RECORDS = (params: {
  platformCode: string
  productCode: string
}) =>
  POST<ProjectMaterialAuditRecordDTO[]>(
    `${HTTP}/projectMaterial/getAuditRecords`,
    params,
    'application/json',
  )

/**
 * 1.3 导出项目物料 （Excel）
 *
 * POST /open/srm/projectMaterial/export
 *
 * @param platformCode 项目编码
 * @param productCode 物料编码（咸亨SKU编码）
 */
export const EXPORT_PROJECT_MATERIAL = (params: ProjectMaterialPageParams) =>
  POST(
    `${HTTP}/projectMaterial/export`,
    {
      ...params,
      supplierCode: GetLocalStorage('SUPPLIER_MDM_CODE'),
    },
    'application/json',
  )
/**
 * 项目物料 API 接口定义
 *
 * 对接文档：《SCP服务商提报结果同步-前端对接接口文档》
 * 基础路径：/mdm-portal（通过网关 /api 代理转发）
 *
 * ======================== 接口总览 ========================
 * 1. POST /open/srm/projectMaterial/getPage           - 项目物料分页查询
 * 2. POST /open/srm/projectMaterial/getAuditRecords   - 项目物料审核记录
 * 3. POST /open/platformProduct/getThirdOnShelf        - 批量获取客户上下架状态（提报管理列表用）
 * =========================================================
 */

import { GetLocalStorage } from '@/tools'
import request from '../tools/request'
import { HTTP } from './http'
const { GET, POST } = request

// ====================== 客户平台状态枚举 ======================

/** 客户平台状态值 → 中文名映射 */
export const THIRD_ON_SHELF_MAP: Record<string, string> = {
  '1': '已上架',
  '2': '已下架',
  '3': '审核中',
  '4': '已驳回',
  '5': '已冻结',
}

// ====================== 数据类型定义 ======================

export interface PageResultProjectMaterialPageDTO {
  /**
   * 分页内容
   */
  content?: ProjectMaterialDTO[]
  /**
   * 当前页码
   */
  pageNo?: number
  /**
   * 分页尺寸
   */
  pageSize?: number
  /**
   * 数据总量
   */
  totalCount?: number
  /**
   * 总页数
   */
  totalPages?: number
  [property: string]: any
}

/** 项目物料分页查询 - 请求参数（Body JSON） */
export interface ProjectMaterialPageParams {
  supplierCode?: string // 服务商编码
  page?: number // 页码，从 1 开始
  limit?: number // 每页条数
  platformCode: string // 项目编码（必填）
  productCodes?: string // 咸亨SKU编码，多个用逗号分隔，上限500
  customerSkuCodes?: string // 客户SKU编码，多个用逗号分隔，上限500
  customerSpuCodes?: string // 客户SPU编码，多个用逗号分隔，上限500
  customerMaterialCodes?: string // 客户物料编码，多个用逗号分隔，上限500
  thirdOnShelf?: string // 客户平台状态：1-已上架 2-已下架 3-审核中 4-已驳回 5-已冻结
  thirdOnShelfStartTime?: number // 状态变更开始时间（毫秒时间戳）
  thirdOnShelfEndTime?: number // 状态变更结束时间（毫秒时间戳）
  productName?: string // 咸亨物料名称（模糊搜索）
  [property: string]: any
}

export interface Pages {}

/** 项目物料列表项（ProjectMaterialDTO） */
export interface ProjectMaterialDTO {
  productCode: string // 咸亨SKU编码
  customerSkuCode: string // 客户SKU编码
  customerSpuCode: string // 客户SPU编码
  customerMaterialCode: string // 客户物料编码
  customerMaterialUnit: string // 客户物资编码单位
  thirdOnShelf: string // 客户平台状态：1-已上架 2-已下架 3-审核中 4-已驳回 5-已冻结
  thirdOnShelfTime: string // 状态变更时间（字符串格式，如 "2026-04-10 14:30:00"）
  pushPrice: string // 推送协议价
  brandName: string // 品牌
  productName: string // 名称
  manuCode: string // 规格/型号
  unitName: string // 基本单位
  description: string // 描述
  [property: string]: any
}

/** 项目物料审核记录（ProjectMaterialAuditRecordDTO） */
export interface ProjectMaterialAuditRecordDTO {
  checkType: string // 审核类型（如：初审上架、新增上架审核、下架通知等）
  checkTypeDesc: string // 审核类型说明
  checkResult: string // 审核结果（如：通过、未通过）
  checkResultDesc: string // 审核结果描述
  checkTime: number // 审核时间（毫秒时间戳）
  remark: string // 审核说明

  [property: string]: any
}

// ====================== API 接口函数 ======================

/**
 * 1.1 项目物料分页查询
 *
 * POST /open/srm/projectMaterial/getPage
 *
 * @param supplierCode 服务商编码（Query 参数）
 * @param params 分页 + 筛选参数（Body JSON）
 */
export const POST_PROJECT_MATERIAL_PAGE = (params: ProjectMaterialPageParams) =>
  POST<PageResultProjectMaterialPageDTO>(
    `${HTTP}/projectMaterial/getPage`,
    {
      ...params,
      supplierCode: GetLocalStorage('SUPPLIER_MDM_CODE'),
    },
    'application/json',
  )

/**
 * 1.2 查询项目物料审核记录

 * 返回按审核时间倒序排列的列表（非分页）
 *
 * @param platformCode 项目编码
 * @param productCode 物料编码（咸亨SKU编码）
 */
export const POST_PROJECT_MATERIAL_AUDIT_RECORDS = (params: {
  platformCode: string
  productCode: string
}) =>
  POST<ProjectMaterialAuditRecordDTO[]>(
    `${HTTP}/projectMaterial/getAuditRecords`,
    params,
    'application/json',
  )

/**
 * 1.3 导出项目物料 （Excel）
 *
 * POST /open/srm/projectMaterial/export
 *
 * @param platformCode 项目编码
 * @param productCode 物料编码（咸亨SKU编码）
 */
export const EXPORT_PROJECT_MATERIAL = (params: ProjectMaterialPageParams) =>
  POST(
    `${HTTP}/projectMaterial/export`,
    {
      ...params,
      supplierCode: GetLocalStorage('SUPPLIER_MDM_CODE'),
    },
    'application/json',
  )
